import { Button, Icon, Modal, Text, Title, TyreInfoBox } from '@wyz/ui';
import { sortBy } from 'lodash-es';
import { Link } from 'react-router';
import { PurchaseErrorCodes } from '~/common/helpers';
import { ICartItem, ICartValidationErrorResponse } from '~/purchase/types';
import { useConfig, Trans, useI18n } from '~/common/context';
// eslint-disable-next-line @wyz/module/no-relative-import
import './unavailable-items-modal.scss';
import { SEARCH_ROUTES } from '~/purchase/helpers';
import { getTyreIdentifiers } from '@wyz/utils';
import { IUserCustomer } from '~/common/types';

type ItemRowProps = {
  item: ICartItem;
  reason: 'UNAVAILABLE' | 'ERROR';
  customer: IUserCustomer;
};

function ItemRow({ item, reason, customer }: ItemRowProps) {
  const { t } = useI18n();
  const { cdnUrl } = useConfig();
  return (
    <tr>
      <td className='p-3'>
        <TyreInfoBox
          tyre={{
            ...item.tyre,
            ...getTyreIdentifiers(
              {
                cai: item.tyre.cai,
                constructorReference: item.tyre.constructorReference,
                alternativeReference: item.tyre.alternativeReference,
              },
              customer.settingTemplate?.generalSetting.tyreIdentifierFields ??
                undefined,
            ),
          }}
          t={t}
          imageBaseUrl={cdnUrl}
          mode='FULL'
        />
      </td>
      <td className='p-3'>
        <Text heading='normal' weight='semi-bold' color='secondary'>
          {item.supplier.name}
        </Text>
      </td>
      <td className='p-3' align='right'>
        <Text heading='normal' weight='semi-bold' color='danger'>
          <Trans
            i18nKey={
              reason === 'UNAVAILABLE'
                ? 'app.purchase.cart_page.unavailable_modal.unavailable_product'
                : 'app.purchase.cart_page.unavailable_modal.technical'
            }
          />
        </Text>
        {reason === 'UNAVAILABLE' && (
          <Text
            heading='xs'
            color='secondary-gray'
            weight='semi-bold'
            strikethrough
          >
            {item.quantity}
          </Text>
        )}
      </td>
    </tr>
  );
}

type Props = {
  items: ICartValidationErrorResponse['errors'];
  onClose: () => void;
  customer: IUserCustomer;
};

function UnavailableItemsModal({ items, onClose, customer }: Props) {
  return (
    <Modal.Container
      onClose={onClose}
      open={items.length !== 0}
      centered
      size='lg'
    >
      <Modal.Header closeButton={true} variant='white'>
        <Title
          heading='xl'
          weight='bold'
          color='secondary'
          className='text-center'
          wrap
        >
          <Trans i18nKey='app.purchase.cart_page.unavailable_modal.title' />
        </Title>
      </Modal.Header>
      <Modal.Body>
        <div className='bg-neutral-grey-1 border rounded border-neutral-grey-4 items-container'>
          <table className='w-100'>
            <thead>
              <tr>
                <td className='p-3' style={{ width: '55%' }}>
                  <Text heading='xs' color='secondary-gray' weight='semi-bold'>
                    <Trans i18nKey='app.purchase.cart_page.table.product' />
                  </Text>
                </td>
                <td className='p-3' style={{ width: '25%' }}>
                  <Text heading='xs' color='secondary-gray' weight='semi-bold'>
                    <Trans i18nKey='app.purchase.cart_page.table.source' />
                  </Text>
                </td>
                <td className='p-3' style={{ width: '20%' }} align='right'>
                  <Text heading='xs' color='secondary-gray' weight='semi-bold'>
                    <Trans i18nKey='app.common.quantity' />
                  </Text>
                </td>
              </tr>
            </thead>
            <tbody>
              {/* Always put ERR_QUANTITY_0 first */}
              {sortBy(items, (o) => !o.errors.ERR_QUANTITY_0).map((error) => (
                <ItemRow
                  key={error.item.id}
                  item={error.item}
                  customer={customer}
                  reason={
                    error.errors[PurchaseErrorCodes.ERR_TECHNICAL_ISSUE]
                      ? 'ERROR'
                      : 'UNAVAILABLE'
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer variant='white'>
        <Button
          id='go-to-search'
          variant='primary'
          as={Link}
          onClick={() => {
            onClose();
          }}
          to={SEARCH_ROUTES.PURCHASE}
          fullWidth
        >
          <Icon icon='reload' size='sm' />
          <Text heading='normal' weight='semi-bold' color='white'>
            <Trans i18nKey='app.purchase.cart_page.unavailable_modal.re_search' />
          </Text>
        </Button>
      </Modal.Footer>
    </Modal.Container>
  );
}

export default UnavailableItemsModal;
